import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { Phone } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { ButtonLink } from '@/components/ui/button-link'
import { hero, phone } from '@/content/site'

const fadeUp = (delay: number) => ({
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
})

export default function Hero() {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%'])
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0])

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900"
    >
      {/* Parallax background layer */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      />

      {/* Ambient background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl animate-float [animation-delay:3s]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32 grid md:grid-cols-2 gap-12 items-center"
      >
        {/* Text content */}
        <div className="text-center md:text-left">
          <motion.p
            {...fadeUp(0.1)}
            className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4"
          >
            Internal Family Systems Coaching
          </motion.p>

          <motion.h1
            {...fadeUp(0.2)}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 font-heading"
          >
            {hero.heading}
          </motion.h1>

          <motion.p
            {...fadeUp(0.4)}
            className="text-xl sm:text-2xl text-slate-300 mb-10 font-light"
          >
            {hero.subheading}
          </motion.p>

          <motion.div
            {...fadeUp(0.6)}
            className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
          >
            <CalendlyBtn
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white border-transparent"
            >
              {hero.cta}
            </CalendlyBtn>
            <ButtonLink
              href={`tel:${phone}`}
              size="lg"
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10 flex items-center gap-2"
            >
              <Phone size={16} />
              {hero.ctaSecondary}
            </ButtonLink>
          </motion.div>
        </div>

        {/* Kinetic profile photo container */}
        <motion.div
          {...fadeUp(0.3)}
          className="flex justify-center md:justify-end"
        >
          <div className="relative">
            {/* Morphing outer glow */}
            <motion.div
              animate={{
                borderRadius: [
                  '60% 40% 30% 70% / 60% 30% 70% 40%',
                  '30% 60% 70% 40% / 50% 60% 30% 60%',
                  '60% 40% 30% 70% / 60% 30% 70% 40%',
                ],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -inset-4 bg-gradient-to-br from-blue-500/20 to-indigo-600/20 blur-xl"
            />
            {/* Floating photo frame */}
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="relative w-64 h-64 sm:w-80 sm:h-80"
            >
              <motion.div
                animate={{
                  borderRadius: [
                    '60% 40% 30% 70% / 60% 30% 70% 40%',
                    '30% 60% 70% 40% / 50% 60% 30% 60%',
                    '60% 40% 30% 70% / 60% 30% 70% 40%',
                  ],
                }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                className="w-full h-full overflow-hidden ring-1 ring-white/10"
              >
                <img
                  src={`${import.meta.env.BASE_URL}profile-photo.jpg`}
                  alt="Douglas P Nelson — IFS Coach"
                  className="w-full h-full object-cover object-center"
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-slate-500 text-xs tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent"
        />
      </motion.div>
    </section>
  )
}
